import type { BoardSummary } from "@mn/shared";
import { type FormEvent, useEffect, useState } from "react";
import { Button, Field, SectionTitle } from "@/components/ui";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";

export const ICONS = [
  "🎯",
  "🚀",
  "💡",
  "📣",
  "📊",
  "📈",
  "🧠",
  "🎨",
  "🖼️",
  "📷",
  "🎬",
  "🎥",
  "🎵",
  "📝",
  "📋",
  "📌",
  "📅",
  "🗓️",
  "✅",
  "⭐",
  "🔥",
  "💎",
  "🏆",
  "🎁",
  "🛍️",
  "🛒",
  "💰",
  "💸",
  "🏷️",
  "📦",
  "✈️",
  "🌎",
  "🏖️",
  "☀️",
  "🌙",
  "❄️",
  "🍕",
  "☕",
  "🍷",
  "🍔",
  "🏠",
  "🏢",
  "🚗",
  "👟",
  "👗",
  "💄",
  "🐶",
  "🐱",
  "🌱",
  "🍀",
  "⚽",
  "🏀",
  "🎮",
  "📱",
  "💻",
  "🔧",
  "🔒",
  "🔔",
  "❤️",
  "🧡",
  "💚",
  "💙",
  "💜",
  "🖤",
  "🤍",
  "🟥",
  "🟧",
  "🟨",
  "🟩",
  "🟦",
  "🟪",
  "⬛",
];
export const ICON_COLORS = [
  "#2a2a2a",
  "#3b3b3b",
  "#5b4b8a",
  "#8a4b6b",
  "#a0522d",
  "#b8860b",
  "#2e7d5b",
  "#1f6f8b",
  "#2f4f9f",
  "#6b7280",
  "#e9c56a",
  "#f4a361",
  "#e77051",
  "#c58af9",
  "#8aa9ff",
  "#55b2a6",
];
export const BACKGROUNDS = [
  "#1d1d1d",
  "#141414",
  "#1a1d24",
  "#1e1a24",
  "#241a1a",
  "#1a2420",
  "#f3f1ec",
  "#ffffff",
];

interface Props {
  board: BoardSummary;
  /** Staff on a non-root board may switch it between team and private. */
  canSetPrivate?: boolean;
  /** Nearest private ancestor (or self) when the board is private by inheritance. */
  privateVia?: { id: string; title: string; ownerName: string | null } | null;
  onClose: () => void;
  onSaved: (b: BoardSummary) => void;
}

/** Title, icon, icon colour and canvas background of a board. */
export function BoardSettingsDialog({ board, canSetPrivate, privateVia, onClose, onSaved }: Props) {
  const t = useT();
  const [title, setTitle] = useState(board.title);
  const [icon, setIcon] = useState<string | null>(board.icon);
  const [iconColor, setIconColor] = useState<string | null>(board.iconColor);
  const [background, setBackground] = useState<string | null>(board.background);
  const [priv, setPriv] = useState(!!board.privateOwnerId);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const b = await api<BoardSummary>(`/api/boards/${board.id}`, {
        method: "PATCH",
        json: {
          title: title.trim() || board.title,
          icon,
          iconColor,
          background,
          ...(canSetPrivate && priv !== !!board.privateOwnerId ? { private: priv } : {}),
        },
      });
      window.dispatchEvent(new CustomEvent("mn:board-updated", { detail: b }));
      onSaved(b);
      onClose();
    } finally {
      setBusy(false);
    }
  }
  const letter = (title.trim()[0] ?? "?").toUpperCase();
  const swatch = (c: string, cur: string | null, set: (v: string | null) => void, label: string) => (
    <button
      key={c}
      type="button"
      title={c}
      aria-label={`${label} ${c}`}
      onClick={() => set(c)}
      className={`size-7 rounded-full border ${cur === c ? "border-text ring-2 ring-text/40" : "border-border"}`}
      style={{ background: c }}
    />
  );
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-black/70 p-4 pt-16"
      onPointerDown={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
    >
      <form
        onSubmit={save}
        className="w-full max-w-lg rounded-md border border-border bg-panel p-6 shadow-panel"
      >
        <SectionTitle>{t.boardSettings}</SectionTitle>
        <div className="mb-4 flex items-center gap-4">
          <div
            className="display flex size-20 shrink-0 items-center justify-center rounded-[18px] border border-border text-4xl text-text"
            style={{ background: iconColor ?? "#2a2a2a" }}
          >
            {icon ?? letter}
          </div>
          <Field
            label={t.boardTitle}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="flex-1"
          />
        </div>
        <div className="mb-1 text-xs uppercase tracking-wide text-dim">{t.boardIcon}</div>
        <div className="mb-3 flex flex-wrap gap-1">
          <button
            type="button"
            onClick={() => setIcon(null)}
            className={`flex size-8 items-center justify-center rounded-sm border text-xs ${icon === null ? "border-text" : "border-border"}`}
            title={t.boardIconLetter}
          >
            {letter}
          </button>
          {ICONS.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => setIcon(e)}
              className={`flex size-8 items-center justify-center rounded-sm border text-lg ${icon === e ? "border-text bg-panel-2" : "border-transparent hover:bg-panel-2"}`}
            >
              {e}
            </button>
          ))}
        </div>
        <div className="mb-1 text-xs uppercase tracking-wide text-dim">{t.boardIconColor}</div>
        <div className="mb-3 flex flex-wrap gap-2">
          {ICON_COLORS.map((c) => swatch(c, iconColor ?? "#2a2a2a", setIconColor, t.boardIconColor))}
          <input
            type="color"
            value={iconColor ?? "#2a2a2a"}
            onChange={(e) => setIconColor(e.target.value)}
            className="size-7 cursor-pointer rounded-full border border-border bg-transparent"
            title={t.customColor}
          />
        </div>
        <div className="mb-1 text-xs uppercase tracking-wide text-dim">{t.boardBackground}</div>
        <div className="mb-5 flex flex-wrap gap-2">
          {BACKGROUNDS.map((c) => swatch(c, background ?? "#1d1d1d", setBackground, t.boardBackground))}
          <input
            type="color"
            value={background ?? "#1d1d1d"}
            onChange={(e) => setBackground(e.target.value)}
            className="size-7 cursor-pointer rounded-full border border-border bg-transparent"
            title={t.customColor}
          />
        </div>
        {canSetPrivate ? (
          <>
            <div className="mb-1 text-xs uppercase tracking-wide text-dim">{t.visibility.label}</div>
            {privateVia && privateVia.id !== board.id ? (
              <p className="mb-5 text-sm text-muted">
                🔒 {t.visibility.via(privateVia.title, privateVia.ownerName)}
              </p>
            ) : (
              <div className="mb-5 flex flex-col gap-2">
                {(
                  [
                    [false, t.visibility.team, t.visibility.teamHint, "👥"],
                    [true, t.visibility.private, t.visibility.privateHint, "🔒"],
                  ] as const
                ).map(([v, label, hint, glyph]) => (
                  <label
                    key={String(v)}
                    className={`flex cursor-pointer items-start gap-3 rounded-sm border p-3 ${priv === v ? "border-text bg-panel-2" : "border-border"}`}
                  >
                    <input
                      type="radio"
                      name="visibility"
                      checked={priv === v}
                      onChange={() => setPriv(v)}
                      className="mt-1"
                    />
                    <span>
                      <span className="block text-sm font-semibold">
                        {glyph} {label}
                      </span>
                      <span className="block text-xs text-muted">{hint}</span>
                    </span>
                  </label>
                ))}
              </div>
            )}
          </>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t.cancel}
          </Button>
          <Button type="submit" loading={busy}>
            {t.save}
          </Button>
        </div>
      </form>
    </div>
  );
}
